import React from "react";

import { prefersReducedMotion } from "../hooks/motion";

const SoundOnIcon = () => (
  <svg
    viewBox={"0 0 24 24"}
    aria-hidden={"true"}
    focusable={"false"}
  >
    <path d={"M4 9v6h4l5 4V5L8 9H4zm12.5 3a4.5 4.5 0 0 0-2.5-4.03v8.05A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06a7 7 0 0 1 0 13.42v2.06a9 9 0 0 0 0-17.54z"} />
  </svg>
);

const SoundOffIcon = () => (
  <svg
    viewBox={"0 0 24 24"}
    aria-hidden={"true"}
    focusable={"false"}
  >
    <path d={"M4 9v6h4l5 4V5L8 9H4zm12.59 3L14 9.41 15.41 8 18 10.59 20.59 8 22 9.41 19.41 12 22 14.59 20.59 16 18 13.41 15.41 16 14 14.59 16.59 12z"} />
  </svg>
);

/*
*  Looping clip of Jo-Ann next to the intro text. Starts muted (browsers only allow muted autoplay) and only plays while
*  it's on screen. Visitors who prefer reduced motion get the normal video controls instead of autoplay.
*/
const IntroVideo = () => {
  const videoRef = React.useRef(null);
  const [isMuted, setIsMuted] = React.useState(true);
  const reducedMotion = React.useMemo(() => prefersReducedMotion(), []);

  //Smaller file on phones
  const src = React.useMemo(() => (
    window.matchMedia && window.matchMedia("(max-width: 767px)").matches ? "/videos/intro-480.mp4" : "/videos/intro-720.mp4"
  ), []);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || reducedMotion || !("IntersectionObserver" in window)) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          //play() is refused if the browser blocks autoplay (e.g. data saver) - the poster stays up
          video.play().catch(() => {});
        }
        else {
          video.pause();
        }
      });
    }, { threshold: 0.25 });
    observer.observe(video);
    return () => observer.disconnect();
  }, [reducedMotion]);

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.muted = !video.muted;
    setIsMuted(video.muted);
    if (!video.muted && video.paused) {
      video.play().catch(() => {});
    }
  };

  return (
    <div className={"intro-video"}>
      <video
        ref={videoRef}
        className={"intro-video-player"}
        src={src}
        poster={"/images/intro-poster.jpg"}
        muted={isMuted}
        loop={true}
        playsInline={true}
        preload={"none"}
        controls={reducedMotion}
        aria-label={"Jo-Ann Strauss introducing the ARTdacity podcast"}
      />
      {reducedMotion ? null : (
        <button
          type={"button"}
          className={"intro-video-sound"}
          onClick={toggleSound}
          aria-pressed={!isMuted}
          aria-label={isMuted ? "Turn sound on" : "Turn sound off"}
        >
          {isMuted ? <SoundOffIcon /> : <SoundOnIcon />}
        </button>
      )}
    </div>
  );
};

export default IntroVideo;
